import { useEffect, useId, useRef, useState } from "react";
import type { Dispatch, ReactElement, SetStateAction } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, AdressAnsicht, AdressAnlegen, AdressBesitzer,
              AdressListe as AdressListeAntwort } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand,
  useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis, Optionen, Schalter } from "@kundeninfo/ui/me-design";
import { formatDatum } from "../format.js";
import { KiKnopf, LoeschKnopf } from "../components/meHuellen.js";
import "./AdressListe.css";

/**
 * Die ADRESSEN eines Objekts — EIN Baustein für drei Einbauorte: den Reiter „Adressen" der
 * Kundenakte, die Kontaktwege am Ansprechpartner und die Adress-Karte des eigenen
 * Unternehmens in der Verwaltung.
 *
 * Ein Baustein und nicht drei Abschriften: Die Regeln, die hier hängen (welche Art welche
 * Rolle trägt, dass die Hauptadresse oben steht, dass das Anlegen im strikten Panel läuft,
 * dass ein Kontextwechsel alles abräumt), gelten an allen drei Stellen gleich. Drei
 * Fassungen liefen beim ersten Nachbessern auseinander — und die unauffälligste davon wäre
 * die dritte, die niemand mehr anfasst. Was sich je Einbauort unterscheidet, steht in den
 * Eigenschaften: der Besitzer, sein Name für die Vorbelegung, das Schreibrecht.
 *
 * **Eigener CSS-Block `adr__`** — kein geteilter Blockname über Ansichten hinweg (Lektion
 * aus `.ka__`). Er gehört allein dieser Datei und reist mit ihr an jeden Einbauort.
 *
 * **Die Fachlichkeit steckt NICHT hier.** Welche Rolle zu welcher Art passt, ob eine ILN
 * ihre Prüfziffer hält, ob eine Hauptadresse gehen darf — das entscheidet der Server
 * (`domain/addressRules.ts`, `repositories/address.ts`). Diese Datei kennt weder die Paare
 * noch die Muster: Die wählbaren Arten und ihre Rollen kommen MIT der Liste
 * (`arten`), und jeder Befund des Servers findet hier seinen Weg an ein Feld. `web` darf
 * `domain` nicht importieren (scripts/check-boundaries.mjs) — und soll es auch nicht:
 * Eine zweite, abgeschriebene Regel wäre eine, die eines Tages anders urteilt als die
 * erste.
 */

/** Nur die fünf Klientenmethoden, die dieser Baustein braucht — so lässt er sich im Test
 *  mit fünf `vi.fn` zeichnen (Muster KundePortalMandanten). */
export type AdressClient = Pick<ApiClient,
  "listAddresses" | "createAddress" | "updateAddress" | "setPrimaryAddress"
  | "archiveAddress">;

/** Eine Art samt ihren zulässigen Rollen, so wie der Server sie mitschickt. */
export type AdressArt = AdressListeAntwort["arten"][number];
type AdressKind = AdressAnsicht["kind"];
type AdressRolle = AdressAnsicht["role"];
type ZeilenArt = AdressAnsicht["lines"][number]["kind"];

export type AdressListeProps = {
  client: AdressClient;
  besitzer: AdressBesitzer;
  /** Das Objekt, dessen Adressen hier stehen. Ein Wechsel setzt ALLES zurück. */
  ownerId: string;
  /** Überschrift und zugleich zugänglicher Name des Abschnitts. */
  titel: string;
  /** Der Name des Objekts (Firmenname, Ansprechpartner) — Vorbelegung der ersten
   *  Anschriftzeile beim Anlegen einer Postanschrift. Ohne Angabe bleibt sie leer. */
  namensVorschlag?: string;
  /** Ohne Schreibrecht (kein ADMIN am eigenen Unternehmen): keine Knöpfe, nur Text. */
  nurLesen?: boolean;
  /** Über diesem Abschnitt steht ein fremdes Panel — dann sind die Knöpfe gesperrt
   *  (dieselbe Überlegung wie `verdeckt` in KundeKonditionen). */
  verdeckt?: boolean;
  /** Sprung in die Akte des Ursprungskunden einer GEERBTEN Adresse (Etappe 2). Ohne
   *  Angabe steht die Herkunft als Text da (Ansprechpartner, eigenes Unternehmen). */
  onKunde?: (id: string) => void;
  /** Nach jeder Änderung: Der Kopf der Kundenakte liest seine Hauptanschrift neu. */
  onGeaendert?: () => void;
};

/**
 * Die deutschen Namen der ROLLEN — reine Beschriftung, deshalb hier und nicht im Vertrag.
 *
 * Der `Record` über `AdressRolle` ist zugleich der Typtest: Kommt in `api-client` eine
 * Rolle hinzu, fehlt hier ein Schlüssel und die Typprüfung schlägt an, statt dass die
 * Maske still den rohen Schlüssel („INVOICE_RECIPIENT") anzeigt. Dasselbe Muster wie bei
 * den Weltnamen der Umgebung. Die Wörter decken sich mit denen der Domäne
 * (`ROLLEN_NAME` in addressRules.ts) — ein Wort für eine Sache, auch über die Pakete hinweg.
 */
const ROLLEN_NAME: Record<AdressRolle, string> = {
  MAIN: "Haupt", BILLING: "Rechnung", DELIVERY: "Lieferung",
  POSTAL_MAIL: "Postweg", INVOICE_RECIPIENT: "Rechnungsempfänger",
  GENERAL: "Allgemein",
};

function rollenName(r: AdressRolle): string {
  return ROLLEN_NAME[r];
}

/** Die Zeilenarten des Anschriftblocks, in der Reihenfolge, in der sie im Block stehen:
 *  erst die Organisation, dann die Person, dazwischen das c/o. Auch hier der Typtest. */
const ZEILENART_NAME: Record<ZeilenArt, string> = {
  ORGANIZATION: "Firmenname",
  UNIT: "Organisationseinheit",
  CARE_OF: "c/o",
  PERSON: "Person",
  FUNCTION: "Funktion",
};
const ZEILENARTEN = Object.keys(ZEILENART_NAME) as ZeilenArt[];

/** Nur UNIT-Zeilen gehen in die E-Rechnung (BT-51/BT-163), und höchstens zwei davon —
 *  die Maske sperrt, was der Server ohnehin mit 422 abwiese. */
const XML_ZEILEN_MAX = 2;
/** Der eine Wert des Kennzeichens „In E-Rechnung“ (me-optionen mit einer Option). */
const IN_XML = "ja";

/**
 * Die Beschriftung des Wertfeldes je Art — sie sagt, WAS hineingehört.
 *
 * Ein VOLLSTÄNDIGER `Record` über alle Arten ausser der Postanschrift (die hat kein
 * Wertfeld, sondern einen Anschriftblock): Kommt in `api-client` eine Art hinzu, fehlt hier
 * ein Schlüssel und die Typprüfung schlägt an — statt dass die Maske still „Wert"
 * beschriftet und niemand merkt, dass die neue Art keine eigene Ansprache hat. Dasselbe
 * gilt für den Hilfetext darunter; `null` heisst dort „braucht keinen", und das ist eine
 * ENTSCHEIDUNG je Art, kein vergessener Eintrag.
 */
type WertArt = Exclude<AdressKind, "POSTAL">;

const WERT_LABEL: Record<WertArt, string> = {
  EMAIL: "E-Mail", PHONE: "Telefon", WEB: "Webseite", ILN: "ILN (GLN)",
  PURCHASE_ORDER: "Bestellnummer",
};

const WERT_HILFE: Record<WertArt, string | null> = {
  EMAIL: null,
  PHONE: "International und ohne Leerzeichen, z. B. +494055441200.",
  WEB: "Vollständig mit https://.",
  ILN: "13 Ziffern; die letzte ist die Prüfziffer (GS1).",
  PURCHASE_ORDER: null,
};

/** Die Postanschrift erreicht diese beiden Wege nie (sie zeichnet den Anschriftblock);
 *  der Rückfall steht trotzdem da, damit die Maske ohne Behauptung auskommt. */
function wertLabel(kind: AdressKind): string {
  return kind === "POSTAL" ? "Wert" : WERT_LABEL[kind];
}
function wertHilfe(kind: AdressKind): string | null {
  return kind === "POSTAL" ? null : WERT_HILFE[kind];
}

type ZeilenEntwurf = { kind: ZeilenArt; text: string; inInvoiceXml: boolean };

/** Der Entwurf im Panel. Die Textfelder sind Zeichenketten — der Schreibweg macht daraus
 *  `null`, wie überall. */
type Entwurf = {
  /** Beim Ändern die Kennung der Verknüpfung, beim Anlegen `null`. */
  id: string | null;
  version: number;
  kind: AdressKind;
  role: AdressRolle;
  value: string;
  label: string;
  note: string;
  validFrom: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  personFirst: boolean;
  lines: ZeilenEntwurf[];
};

/**
 * Die Felder der Maske, an die ein Befund des Servers gehen kann.
 *
 * `kind` steht bewusst NICHT darin, obwohl der Server es beanstanden kann (422
 * ART_UNVERAENDERLICH): Die Art ist beim Ändern ein gesperrtes Feld und beim Anlegen eine
 * Auswahl aus genau dem, was der Server mitgeschickt hat — ein Befund daran ist kein
 * Eingabefehler, sondern ein Widerspruch zwischen Maske und Server. Der gehört an die
 * Fussleiste, wo er als Satz gelesen wird, und nicht als roter Strich unter ein Feld, an
 * dem sich nichts richten lässt.
 */
type FeldName = "role" | "value" | "label" | "note" | "validFrom"
  | "street" | "houseNumber" | "postalCode" | "city" | "country" | "lines";

const FELDNAMEN: readonly FeldName[] = [
  "role", "value", "label", "note", "validFrom",
  "street", "houseNumber", "postalCode", "city", "country", "lines",
];

function leererEntwurf(arten: AdressArt[], namensVorschlag: string | undefined): Entwurf {
  const erste = arten.find((a) => a.rollen.length > 0);
  const kind = erste?.key ?? "POSTAL";
  return {
    id: null, version: 0,
    kind, role: erste?.rollen[0] ?? "MAIN",
    value: "", label: "", note: "", validFrom: "",
    street: "", houseNumber: "", postalCode: "", city: "", country: "DE",
    personFirst: false,
    // Eine Postanschrift beginnt mit dem Namen des Objekts: Er steht auf jedem Brief, und
    // ihn abzutippen wäre die erste Gelegenheit, ihn anders zu schreiben als oben im Kopf.
    lines: erste?.hatPostanschrift === true
      ? [{ kind: "ORGANIZATION", text: namensVorschlag ?? "", inInvoiceXml: false }]
      : [],
  };
}

function entwurfAus(a: AdressAnsicht): Entwurf {
  return {
    id: a.id, version: a.version, kind: a.kind, role: a.role,
    value: a.value ?? "", label: a.label ?? "", note: a.note ?? "",
    validFrom: a.validFrom ?? "",
    street: a.postal?.street ?? "", houseNumber: a.postal?.houseNumber ?? "",
    postalCode: a.postal?.postalCode ?? "",
    city: a.postal?.city ?? "", country: a.postal?.country ?? "DE",
    personFirst: a.postal?.personFirst ?? false,
    lines: a.lines.map((z) => ({ kind: z.kind, text: z.text,
                                 inInvoiceXml: z.inInvoiceXml })),
  };
}

function leer(wert: string): string | null {
  return wert.trim() === "" ? null : wert.trim();
}

/**
 * Was der Schreibweg schickt — und die Spur zurück.
 *
 * Leere Zeilen fallen heraus (die Maske hält sie als Eingabeplatz vor, gespeichert wären
 * sie eine Lücke im Block). Damit stimmen die Plätze im Rumpf nicht mehr mit denen in der
 * Maske überein — und genau darauf zeigt der Server, wenn er `lines[1].inInvoiceXml`
 * beanstandet. `zeilenPlatz[i]` sagt deshalb, welche Zeile der MASKE die i-te Zeile des
 * Rumpfes war; ohne diese Spur landete ein Befund an der falschen Zeile, und zwar ohne
 * jedes Anzeichen.
 */
function alsEingabe(e: Entwurf, hatPostanschrift: boolean):
  { eingabe: AdressAnlegen; zeilenPlatz: number[] } {
  const zeilenPlatz: number[] = [];
  const lines: AdressAnlegen["lines"] = [];
  if (hatPostanschrift) {
    e.lines.forEach((z, i) => {
      if (z.text.trim() === "") return;
      lines.push({ kind: z.kind, text: z.text.trim(), sortIndex: lines.length,
                   inInvoiceXml: z.inInvoiceXml });
      zeilenPlatz.push(i);
    });
  }
  return {
    eingabe: {
      kind: e.kind, role: e.role,
      value: hatPostanschrift ? null : leer(e.value),
      label: leer(e.label), note: leer(e.note), validFrom: leer(e.validFrom),
      postal: hatPostanschrift ? {
        street: leer(e.street), houseNumber: leer(e.houseNumber),
        postalCode: leer(e.postalCode), city: leer(e.city),
        country: e.country, personFirst: e.personFirst,
      } : null,
      lines,
    },
    zeilenPlatz,
  };
}

/**
 * Wie diese Adresse in einer Rückfrage und in einem zugänglichen Namen heisst — die
 * UNTERSCHEIDENDE Angabe zuerst.
 *
 * Die Reihenfolge ist nicht beliebig: Ein Objekt hat mehrere Adressen derselben Art, und
 * der Name muss sagen, WELCHE gemeint ist. Die Bezeichnung („Zentrale", „Lager Süd") ist
 * genau dafür vergeben worden und steht deshalb vorn. Dann der Wert (jede E-Mail ist eine
 * andere). Bei einer Postanschrift wäre die erste Zeile der FIRMENNAME — und der ist an
 * allen Anschriften desselben Kunden gleich: „Nordlicht Logistik GmbH archivieren" stünde
 * dann dreimal auf dem Bildschirm. Also Strasse und Ort, die Angaben, in denen sich zwei
 * Anschriften tatsächlich unterscheiden.
 */
function bezeichnung(a: AdressAnsicht): string {
  if (a.label !== null) return a.label;
  if (a.value !== null) return a.value;
  if (a.postal !== null) {
    const ort = [a.postal.street, a.postal.city]
      .filter((t): t is string => t !== null && t !== "").join(", ");
    if (ort !== "") return ort;
  }
  return a.zeilen[0] ?? "Adresse ohne Angabe";
}

/**
 * Die Hauptadresse ihrer Rolle steht oben (Hausregel „das Wichtigste zuerst").
 *
 * Der Server ordnet bereits so (`ORDNUNG` in repositories/address.ts), und gegen seine
 * Antwort ist das hier ein Leerlauf. Die Zusage gehört trotzdem in die Ansicht: Sie ist
 * eine Eigenschaft des BILDES, und ein Bild, das sich auf eine SQL-Zeile in einem anderen
 * Paket verlässt, hält sie nur so lange, wie niemand dort sortiert.
 *
 * Innerhalb einer Rolle, nicht innerhalb der Art: Eine Art trägt mehrere Rollen, und jede
 * hat ihre eigene Hauptadresse — die Rollen untereinander bleiben in der Reihenfolge, in
 * der der Server sie liefert (sein `sortIndex`).
 */
function hauptZuerst(liste: AdressAnsicht[]): AdressAnsicht[] {
  const rollen: AdressRolle[] = [];
  for (const e of liste) if (!rollen.includes(e.role)) rollen.push(e.role);
  return rollen.flatMap((r) => {
    const derRolle = liste.filter((e) => e.role === r);
    return [...derRolle.filter((e) => e.isPrimary), ...derRolle.filter((e) => !e.isPrimary)];
  });
}

/**
 * Eigene Adressen vor geerbten — UND je Herkunfts-Gruppe wieder die Hauptadresse oben
 * (Fixrunde 1, Ruling zu Aufgabe 6).
 *
 * `hauptZuerst` NACH der Herkunft anzuwenden wäre falsch gewesen: Eine geerbte
 * Hauptadresse hätte dann eine eigene Nebenadresse derselben Art/Rolle verdrängt — genau
 * das Gegenteil von „was am Objekt selbst steht, gilt vor dem, was von oben kommt"
 * (Global Constraint). Deshalb wird ZUERST nach Herkunft geteilt und ERST DANACH je
 * Teilliste sortiert: `[...hauptZuerst(eigene), ...hauptZuerst(geerbte)]`.
 */
function eigeneZuerst(a: AdressAnsicht[]): AdressAnsicht[] {
  const eigene = a.filter((e) => e.origin === null);
  const geerbte = a.filter((e) => e.origin !== null);
  return [...hauptZuerst(eigene), ...hauptZuerst(geerbte)];
}

/**
 * Der Befund des Servers an seinen Platz in der Maske.
 *
 * Der Server benennt seine Felder so, wie der RUMPF gebaut ist: `city` flach,
 * `postal.city` unter dem Schema, `lines[1].inInvoiceXml` an einer bestimmten Zeile.
 * Diese Funktion kennt alle drei Formen; was sie nicht unterbringt, sagt sie mit `null` —
 * der Aufrufer stellt es dann an die Fussleiste, damit NICHTS unsichtbar bleibt.
 */
function zielFeld(name: string, zeilenPlatz: number[]):
  { feld: FeldName } | { zeile: number } | null {
  const zeile = /^lines\[(\d+)\]/.exec(name);
  if (zeile !== null) {
    const platz = zeilenPlatz[Number(zeile[1])];
    return platz === undefined ? null : { zeile: platz };
  }
  const flach = name.startsWith("postal.") ? name.slice("postal.".length) : name;
  return (FELDNAMEN as readonly string[]).includes(flach)
    ? { feld: flach as FeldName } : null;
}

export function AdressListe(p: AdressListeProps): ReactElement {
  /** `null` heisst „noch nicht geladen" — von „geladen und leer" zu unterscheiden. */
  const [eintraege, setEintraege] = useState<AdressAnsicht[] | null>(null);
  const [arten, setArten] = useState<AdressArt[]>([]);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Entwurf | null>(null);
  const [ausgang, setAusgang] = useState<Entwurf | null>(null);
  /** Meldungen AUSSERHALB des Panels (Hauptadresse, Archivieren, Nachladen). Die des
   *  Speicherns führt `formular.meldung`. */
  const [meldung, setMeldung] = useState<string | null>(null);
  /** Der Platz jeder mitgeschickten Zeile in der Maske (aus `alsEingabe`) — damit ein
   *  Befund `lines[n]` an die richtige ZEILE findet, auch wenn leere Zeilen fehlten. */
  const zeilenPlatz = useRef<number[]>([]);
  /** „Meine Eingaben behalten“: nur die Versionsbasis vorrücken (siehe KundeStammdaten). */
  const eingabenBehalten = useRef(false);
  /** Eigene Kennungen je Einbauort: Der Baustein steht an drei Stellen, und zwei gleiche
   *  `id`-Werte auf einer Seite brächen die Verbindung von Beschriftung und Feld. */
  const idp = useId();

  /**
   * Die laufende NUMMER dieses Abschnitts — der Riegel gegen Antworten, die zu spät kommen
   * (Muster KundePortalMandanten). Jeder Abruf merkt sich beim Start die Nummer und
   * schreibt sein Ergebnis nur, wenn sie beim Eintreffen noch dieselbe ist. Der
   * Besitzerwechsel und das Abräumen zählen sie hoch und machen ALLE fliegenden Antworten
   * ungültig — auch die der Nachlade-Abrufe nach Speichern und Archivieren.
   */
  const lauf = useRef(0);
  const { client, besitzer, ownerId, onKunde } = p;
  const listAddresses = client.listAddresses;

  useEffect(() => {
    const meiner = ++lauf.current;
    const nochAktuell = (): boolean => meiner === lauf.current;
    setEintraege(null);
    // Auch die ARTEN zurück: Sie gehören zum Besitzer (die Bestellnummer gibt es nur am
    // Beleg, `artenFuer` in routes/addresses.ts). Blieben die alten stehen, böte das
    // Anlege-Panel für einen Augenblick eine Art an, die dieser Besitzer nicht trägt.
    setArten([]);
    setLadeFehler(null);
    setDialog(null);
    setAusgang(null);
    setMeldung(null);
    listAddresses(besitzer, ownerId)
      .then((antwort) => {
        if (!nochAktuell()) return;
        setEintraege(antwort.items);
        setArten(antwort.arten);
      })
      .catch((fehler: unknown) => {
        if (!nochAktuell()) return;
        setEintraege([]);
        setLadeFehler(fehler instanceof ApiError ? fehler.message
          : "Die Adressen konnten nicht geladen werden.");
      });
    return () => { lauf.current += 1; };
  }, [listAddresses, besitzer, ownerId]);

  /**
   * Formularzustände des Panels (ME-Etappe 3, Aufgabe 3): `laeuft` sperrt Felder und Fuß,
   * eine 422 legt ihre Befunde an Feld oder ZEILE (`zielFeld`) und der Fokus springt auf
   * den ersten — auf einem Telefon liegt er oft ausserhalb des Bildes, ohne den Sprung stünde
   * die Maske scheinbar unverändert da. Was die Maske nicht unterbringt (`kind` beim
   * Ändern, unbekannte Felder), steht als Satz im Panel: NICHTS verschwindet still. Eine
   * 409 beim Ändern hält die Eingabe als „fremder Stand“ fest (Neu laden / Meine Eingaben
   * behalten) — früher stand nur die Meldung da.
   */
  const formular = useFormularZustand<true>({
    speichern: async () => {
      const e = dialog;
      if (e === null) throw new Error("Kein Panel offen.");
      const { eingabe, zeilenPlatz: platz } = alsEingabe(e, hatPostanschrift);
      zeilenPlatz.current = platz;
      if (e.id === null) await client.createAddress(besitzer, ownerId, eingabe);
      else {
        await client.updateAddress(besitzer, ownerId, e.id,
          { ...eingabe, version: e.version });
      }
      return true;
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      const id = dialog?.id ?? null;
      const meiner = lauf.current;
      const frisch = await listAddresses(besitzer, ownerId);
      if (meiner !== lauf.current) return;
      setEintraege(frisch.items);
      setArten(frisch.arten);
      const neu = frisch.items.find((x) => x.id === id);
      if (neu === undefined) {
        setDialog(null);
        setAusgang(null);
        setMeldung("Diese Adresse gibt es inzwischen nicht mehr.");
        return;
      }
      if (behalten) {
        setDialog((d) => (d === null ? d : { ...d, version: neu.version }));
      } else {
        setDialog(entwurfAus(neu));
        setAusgang(entwurfAus(neu));
      }
    },
    bekannteFelder: (feld) => zielFeld(feld, zeilenPlatz.current) !== null,
  });
  const speichert = formular.laeuft;
  // Die Befunde der 422 an ihren Platz in der Maske (Feld oder Zeile, siehe `zielFeld`).
  const feldFehler: Partial<Record<FeldName, string>> = {};
  const zeilenFehler: Record<number, string> = {};
  for (const [name, text] of Object.entries(formular.feldFehler)) {
    const ziel = zielFeld(name, zeilenPlatz.current);
    if (ziel === null) continue;
    if ("feld" in ziel) feldFehler[ziel.feld] ??= text;
    else zeilenFehler[ziel.zeile] ??= text;
  }

  const geaendert = dialog !== null && ausgang !== null
    && JSON.stringify(dialog) !== JSON.stringify(ausgang);
  useBeforeUnload(geaendert);

  const art = arten.find((a) => a.key === dialog?.kind);
  const hatPostanschrift = art?.hatPostanschrift === true;
  const waehlbar = arten.filter((a) => a.rollen.length > 0);

  /**
   * Die Liste nach einer erfolgreichen Änderung neu holen — in EIGENER Obhut.
   *
   * Das Nachladen läuft bewusst nicht im `try` des Schreibwegs: Ein Fehler dort sähe aus
   * wie ein Fehler beim SCHREIBEN. Das Panel bliebe mit „Speichern" stehen, der Bearbeiter
   * drückte noch einmal — und legte die Adresse ein zweites Mal an. Deshalb ist die Sache
   * nach dem Schreiben ENTSCHIEDEN, und was hier schiefgeht, sagt genau das: Es ist
   * gespeichert, nur das Bild ist alt.
   */
  async function neuLaden(meiner: number, beiFehler: string): Promise<void> {
    try {
      const frisch = await listAddresses(besitzer, ownerId);
      if (meiner !== lauf.current) return;
      setEintraege(frisch.items);
      setArten(frisch.arten);
    } catch {
      if (meiner !== lauf.current) return;
      setMeldung(beiFehler);
    }
  }

  function oeffnen(vorlage: AdressAnsicht | null): void {
    const e = vorlage === null
      ? leererEntwurf(waehlbar, p.namensVorschlag) : entwurfAus(vorlage);
    setDialog(e);
    setAusgang(e);
    setMeldung(null);
    formular.zuruecksetzen();
  }

  /** Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel"): kein Ausgang
   *  unter laufendem Schreiben, Rückfrage bei ungespeicherten Eingaben. */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    setDialog(null);
    setAusgang(null);
  }

  async function beiSpeichern(): Promise<void> {
    if (dialog === null) return;
    const meiner = lauf.current;
    const ok = await formular.aktion();
    if (ok !== true || meiner !== lauf.current) return;
    // Das Panel geht SOFORT zu — die Adresse steht, und alles danach kann daran nichts
    // mehr ändern. Ein Panel, das bis zum Ende des Nachladens offen bliebe, lüde bei einem
    // klemmenden GET zum zweiten Klick auf „Speichern" ein: die Doppelanlage.
    setDialog(null);
    setAusgang(null);
    p.onGeaendert?.();
    // Nach dem Erfolg den SERVERSTAND holen statt die Antwort anzuhängen: Die Liste ist
    // sortiert (Hauptadresse zuoberst, dann Art und Rolle), und ein angehängter Satz
    // stünde am falschen Platz — bis zum nächsten Laden, das ihn wortlos verschöbe.
    await neuLaden(meiner,
      "Gespeichert. Die Liste konnte nicht neu geladen werden.");
  }

  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void formular.neuLaden();
  }

  async function beiHauptadresse(a: AdressAnsicht): Promise<void> {
    const meiner = lauf.current;
    setMeldung(null);
    try {
      await client.setPrimaryAddress(besitzer, ownerId, a.id);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      setMeldung(fehler instanceof ApiError ? fehler.message
        : "Die Hauptadresse konnte nicht umgehängt werden.");
      return;
    }
    if (meiner !== lauf.current) return;
    p.onGeaendert?.();
    await neuLaden(meiner,
      "Die Hauptadresse steht. Die Liste konnte nicht neu geladen werden.");
  }

  const { bestaetigen } = useBestaetigung();
  async function beiArchivieren(a: AdressAnsicht): Promise<void> {
    // Die Rückfrage NENNT die Adresse: „Wirklich archivieren?" liesse offen, welche der
    // fünf Zeilen gemeint ist.
    if (!(await bestaetigen({
      titel: "Adresse archivieren?",
      text: `Adresse archivieren: ${bezeichnung(a)}. Sie verschwindet aus der Liste, bleibt `
        + "auf festgeschriebenen Belegen aber erhalten. Wirklich archivieren?",
      bestaetigenText: "Archivieren",
    }))) return;
    const meiner = lauf.current;
    setMeldung(null);
    try {
      await client.archiveAddress(besitzer, ownerId, a.id);
    } catch (fehler) {
      if (meiner !== lauf.current) return;
      // Der häufigste Fall ist die 422 HAUPTADRESSE_FEHLT: Sie ERKLÄRT, was zuerst zu tun
      // ist, und gehört deshalb im Wortlaut des Servers hierher. Dasselbe gilt für die
      // 422 ADRESSE_AM_BELEG_GEWAEHLT (Fixrunde nach Gesamtreview, I1): Sie NENNT die
      // Belege, an denen die Adresse hängt — ein eigener Satz hier könnte das nicht, weil
      // die Maske die Belege nicht kennt.
      setMeldung(fehler instanceof ApiError ? fehler.message
        : `${bezeichnung(a)} konnte nicht archiviert werden.`);
      return;
    }
    if (meiner !== lauf.current) return;
    p.onGeaendert?.();
    await neuLaden(meiner,
      "Archiviert. Die Liste konnte nicht neu geladen werden.");
  }

  const schreibbar = p.nurLesen !== true;
  const gesperrt = dialog !== null || p.verdeckt === true;

  return (
    <section className="adr" aria-label={p.titel}>
      <h3 className="adr__titel">{p.titel}</h3>

      {meldung !== null && dialog === null && <Hinweis art="fehler">{meldung}</Hinweis>}
      {ladeFehler !== null && <Hinweis art="fehler">{ladeFehler}</Hinweis>}
      {eintraege === null && <p className="adr__hinweis" role="status">Lädt …</p>}
      {eintraege !== null && eintraege.length === 0 && ladeFehler === null && (
        <p className="adr__hinweis">
          Für diesen Datensatz ist noch keine Adresse hinterlegt.
        </p>
      )}

      {eintraege !== null && arten
        .filter((a) => eintraege.some((e) => e.kind === a.key))
        .map((a) => (
          /* Eine Gruppe je ART — sie ist die Ordnung, in der ein Mensch sucht („wo ist die
             Telefonnummer?"), nicht die Rolle. Die Rolle steht an der Zeile. */
          <div key={a.key} className="adr__gruppe" role="group" aria-label={a.name}>
            <h4 className="adr__gruppentitel">{a.name}</h4>
            <ul className="adr__liste">
              {eigeneZuerst(eintraege.filter((e) => e.kind === a.key)).map((e) => (
                /* `key` am OBJEKT, nicht am Listenplatz: Die Liste ist sortiert und rückt
                   beim Archivieren zusammen. */
                <li key={e.id} className="adr__zeile">
                  <div className="adr__inhalt">
                    {e.zeilen.length > 0
                      ? e.zeilen.map((z, i) => (
                          <span key={`${e.id}-${i}`} className="adr__anschriftzeile">{z}</span>))
                      : <span className="adr__wert">{e.value}</span>}
                    {e.label !== null && (
                      <span className="adr__etikett">{e.label}</span>)}
                  </div>
                  <div className="adr__marken">
                    {/* Die Markierung ist ein WORT und nicht nur eine Farbe — sonst bliebe
                        sie für einen Bildschirmleser unsichtbar. */}
                    {e.isPrimary && (
                      <Chip className="adr__marke adr__marke--haupt">Hauptadresse</Chip>)}
                    <Chip className="adr__marke">{rollenName(e.role)}</Chip>
                    {e.validFrom !== null && (
                      <Chip className="adr__marke">ab {formatDatum(e.validFrom)}</Chip>)}
                  </div>
                  {e.origin !== null && (
                    <div className="adr__herkunft">
                      {onKunde !== undefined ? (
                        <KiKnopf art="leise" className="adr__herkunft-verweis"
                          name={`${e.origin.companyName}, anzeigen`}
                          onClick={() => onKunde(e.origin!.customerId)}>
                          von {e.origin.companyName}
                        </KiKnopf>
                      ) : (
                        <span className="adr__herkunft-text">
                          von {e.origin.companyName}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Eine geerbte Adresse ist hier sichtbar und nicht änderbar (Spec) —
                      und über diesen Kunden ohnehin nicht adressierbar (die Route findet
                      die Verknüpfung nicht an ihm und antwortete 404). */}
                  {schreibbar && e.origin === null && (
                    <div className="adr__aktionen">
                      <KiKnopf className="adr__knopf" deaktiviert={gesperrt}
                        name={`${bezeichnung(e)} ändern`}
                        onClick={() => oeffnen(e)}>Ändern</KiKnopf>
                      {!e.isPrimary && (
                        <KiKnopf className="adr__knopf" deaktiviert={gesperrt}
                          name={`${bezeichnung(e)} zur Hauptadresse machen`}
                          onClick={() => { void beiHauptadresse(e); }}>
                          Zur Hauptadresse
                        </KiKnopf>
                      )}
                      <KiKnopf className="adr__knopf" deaktiviert={gesperrt}
                        name={`${bezeichnung(e)} archivieren`}
                        onClick={() => { void beiArchivieren(e); }}>Archivieren</KiKnopf>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

      {schreibbar && (
        <div className="adr__fuss">
          <KiKnopf className="adr__knopf"
            deaktiviert={eintraege === null || gesperrt}
            onClick={() => oeffnen(null)}>Adresse hinzufügen</KiKnopf>
        </div>
      )}

      {dialog !== null && (
        <Modal titel={dialog.id === null ? "Adresse hinzufügen" : "Adresse ändern"}
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert} breite="mittel">
          {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={formular.zustand === "fremder-stand" ? (
              <span>
                Diese Adresse wurde zwischenzeitlich geändert. Deine Eingaben stehen noch da —
                „Neu laden“ übernimmt den aktuellen Stand.{" "}
                <KiKnopf className="ki-eingaben-behalten"
                  onClick={eigeneEingabenBehalten}>
                  Meine Eingaben behalten
                </KiKnopf>
              </span>
            ) : undefined}>
            {/* Beim Ändern bleibt die Art, was sie ist — der Server weist einen Wechsel mit
                422 ART_UNVERAENDERLICH ab, und ein Feld, das nichts bewirken kann, gehört
                gesperrt. */}
            <Auswahl id={`${idp}-kind`} name={`${idp}-kind`} beschriftung="Adressart"
              wert={dialog.kind} gesperrt={dialog.id !== null}
              onGeaendert={(ev) => {
                const neueArt = arten.find((a) => a.key === ev.wert);
                if (neueArt === undefined) return;
                setDialog((d) => (d === null ? d : {
                  ...d, kind: neueArt.key, role: neueArt.rollen[0] ?? "MAIN",
                  // Die Zeilen gehören an eine Postanschrift; eine E-Mail-Adresse mit
                  // Anschriftzeilen wiese der Server ab (`zeilenFehler` in domain).
                  lines: neueArt.hatPostanschrift
                    ? (d.lines.length > 0 ? d.lines
                      : [{ kind: "ORGANIZATION", text: p.namensVorschlag ?? "",
                           inInvoiceXml: false }])
                    : [],
                }));
              }}>
              {waehlbar.map((a) => (
                <option key={a.key} value={a.key}>{a.name}</option>))}
            </Auswahl>

            <Auswahl id={`${idp}-role`} name={`${idp}-role`} beschriftung="Rolle"
              wert={dialog.role}
              {...(feldFehler.role !== undefined ? { fehler: feldFehler.role } : {})}
              onGeaendert={(ev) => {
                const wert = ev.wert as AdressRolle;
                setDialog((d) => (d === null ? d : { ...d, role: wert }));
              }}>
              {(art?.rollen ?? ["MAIN"]).map((r) => (
                <option key={r} value={r}>{rollenName(r)}</option>))}
            </Auswahl>

            {hatPostanschrift ? (
              <>
                {/* Straße und Hausnummer stehen NEBENEINANDER, nicht untereinander: Sie
                    bilden auf dem Briefumschlag eine Zeile, und zwei volle Zeilen im
                    Formular liessen sie wie zwei Anschriftteile aussehen. Auf schmalem
                    Bildschirm bricht der Zweispalter um (siehe .adr__strassenzeile). */}
                <div className="adr__strassenzeile">
                  <Feld id={`${idp}-street`} label="Straße" bearbeitbar
                    wert={dialog.street}
                    onChange={(w) => setDialog((d) => (d === null ? d : { ...d, street: w }))}
                    {...(feldFehler.street !== undefined
                      ? { fehler: feldFehler.street } : {})} />
                  <Feld id={`${idp}-houseNumber`} label="Hausnummer" bearbeitbar
                    wert={dialog.houseNumber}
                    onChange={(w) =>
                      setDialog((d) => (d === null ? d : { ...d, houseNumber: w }))}
                    {...(feldFehler.houseNumber !== undefined
                      ? { fehler: feldFehler.houseNumber } : {})} />
                </div>
                <Feld id={`${idp}-postalCode`} label="Postleitzahl" bearbeitbar
                  wert={dialog.postalCode}
                  onChange={(w) =>
                    setDialog((d) => (d === null ? d : { ...d, postalCode: w }))}
                  {...(feldFehler.postalCode !== undefined
                    ? { fehler: feldFehler.postalCode } : {})} />
                <Feld id={`${idp}-city`} label="Ort" bearbeitbar wert={dialog.city}
                  onChange={(w) => setDialog((d) => (d === null ? d : { ...d, city: w }))}
                  {...(feldFehler.city !== undefined ? { fehler: feldFehler.city } : {})} />
                <Feld id={`${idp}-country`} label="Land (ISO-Code)" bearbeitbar
                  wert={dialog.country}
                  onChange={(w) => setDialog((d) => (d === null ? d
                    : { ...d, country: w.toUpperCase() }))}
                  {...(feldFehler.country !== undefined
                    ? { fehler: feldFehler.country } : {})} />
                <Schalter id={`${idp}-personFirst`} name={`${idp}-personFirst`}
                  beschriftung="Person über der Firma" an={dialog.personFirst}
                  onGeaendert={(ev) =>
                    setDialog((d) => (d === null ? d : { ...d, personFirst: ev.an }))} />
                {/* DIN 5008 setzt die Firma nach oben; persönliche Post dreht das um. */}
                <p className="adr__hilfetext">
                  Ohne Haken steht der Firmenblock oben (DIN 5008).
                </p>
                <AnschriftZeilen zeilen={dialog.lines} idp={idp} setDialog={setDialog}
                  fehler={feldFehler.lines} zeilenFehler={zeilenFehler} />
              </>
            ) : (
              <>
                <Feld id={`${idp}-value`} label={wertLabel(dialog.kind)}
                  bearbeitbar wert={dialog.value}
                  onChange={(w) => setDialog((d) => (d === null ? d : { ...d, value: w }))}
                  {...(feldFehler.value !== undefined
                    ? { fehler: feldFehler.value } : {})} />
                {wertHilfe(dialog.kind) !== null && (
                  <p className="adr__hilfetext">{wertHilfe(dialog.kind)}</p>)}
              </>
            )}

            <Feld id={`${idp}-label`} label="Bezeichnung" bearbeitbar wert={dialog.label}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, label: w }))}
              {...(feldFehler.label !== undefined ? { fehler: feldFehler.label } : {})} />
            <Feld id={`${idp}-validFrom`} label="Gültig ab" typ="date" bearbeitbar
              wert={dialog.validFrom}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, validFrom: w }))}
              {...(feldFehler.validFrom !== undefined
                ? { fehler: feldFehler.validFrom } : {})} />
            <Feld id={`${idp}-note`} label="Notiz" bearbeitbar wert={dialog.note}
              onChange={(w) => setDialog((d) => (d === null ? d : { ...d, note: w }))}
              {...(feldFehler.note !== undefined ? { fehler: feldFehler.note } : {})} />
          </Formular>

          {/* Die zwei einzigen Ausgänge. `abbrechenGesperrt` steht an BEIDEN Stellen — am
              Rahmen für Escape, hier für den Knopf. */}
          <ModalFuss speichernText="Speichern"
            speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void beiSpeichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </section>
  );
}

type AnschriftZeilenProps = {
  zeilen: ZeilenEntwurf[];
  idp: string;
  setDialog: Dispatch<SetStateAction<Entwurf | null>>;
  /** Ein Befund, der den BLOCK als ganzen betrifft (zu viele Kreuze, unbekannte Art). */
  fehler: string | undefined;
  /** Befunde an einzelnen Zeilen, am Platz der MASKE (siehe `alsEingabe`). */
  zeilenFehler: Record<number, string>;
};

/**
 * Der Anschriftblock im Panel — die Zeilen über Straße und Ort: Firmenname,
 * Organisationseinheit, c/o, Person, Funktion.
 *
 * Er ist eine Liste und keine feste Zahl von Feldern, weil eine Anschrift nicht zwei
 * Zusatzzeilen hat, sondern so viele, wie der Brief braucht („Abteilung Einkauf", „z. Hd.
 * Frau Meyer", „c/o Nordlicht Verwaltung"). Die REIHENFOLGE in der Maske ist die im Block:
 * Was hier oben steht, steht auf dem Brief oben (`sortIndex` vergibt der Schreibweg
 * lückenlos neu, siehe `alsEingabe`).
 *
 * Die Beschriftungen der Spalten stehen EINMAL über der Liste; die Felder selbst tragen
 * ihren zugänglichen Namen mitsamt der Zeilennummer, damit ein Bildschirmleser „Text der
 * Zeile 2" sagt und nicht dreimal „Text".
 */
function AnschriftZeilen(p: AnschriftZeilenProps): ReactElement {
  const imXml = p.zeilen.filter((z) => z.inInvoiceXml).length;

  function aendern(i: number, teil: Partial<ZeilenEntwurf>): void {
    p.setDialog((d) => (d === null ? d : {
      ...d,
      lines: d.lines.map((z, j) => {
        if (j !== i) return z;
        const neu = { ...z, ...teil };
        // Das Kennzeichen hängt an der ORGANISATIONSEINHEIT (BT-51/BT-163). Wandert die
        // Zeile zu einer anderen Art, wandert es nicht mit: Es bliebe sonst unsichtbar
        // gesetzt (das Kästchen ist dann gesperrt) und liefe in eine 422.
        return neu.kind === "UNIT" ? neu : { ...neu, inInvoiceXml: false };
      }),
    }));
  }

  return (
    <div className="adr__zeilenblock">
      <h5 className="adr__blocktitel">Anschriftzeilen</h5>
      <div className="adr__zeilenkopf" aria-hidden="true">
        <span>Zeilenart</span><span>Text</span><span /><span />
      </div>
      {p.zeilen.map((z, i) => (
        // Der Platz IST die Kennung: Eine Zeile hat keine eigene, und die Reihenfolge ist
        // gerade das, was hier bearbeitet wird.
        <div key={i} className="adr__zeilenentwurf" role="group"
          aria-label={`Anschriftzeile ${i + 1}`}>
          <Auswahl kompakt beschriftung={`Zeilenart der Zeile ${i + 1}`} wert={z.kind}
            onGeaendert={(ev) => aendern(i, { kind: ev.wert as ZeilenArt })}>
            {ZEILENARTEN.map((k) => (
              <option key={k} value={k}>{ZEILENART_NAME[k]}</option>))}
          </Auswahl>
          {/* Übergang P12-7 (docs/ME-DESIGN.md), seit ME-Etappe 3: der Text der Zeile bleibt
              ein rohes Feld —
              im Zeilenraster steht die Beschriftung EINMAL über der Spalte, und me-eingabe
              zeigt ihre Beschriftung immer sichtbar (kein `kompakt`).
              Der Befund hängt per aria-invalid am Text; dorthin springt der Fokus
              (me-formular `zumErstenFehler`) — bei einem Befund am ganzen Block an die
              erste Zeile, die nächstgelegene Stelle, an der sich etwas ändern lässt. */}
          <input id={`${p.idp}-line-${i}-text`} className="ki-feld__eingabe" type="text"
            value={z.text} aria-label={`Text der Zeile ${i + 1}`}
            aria-invalid={p.zeilenFehler[i] !== undefined
              || (i === 0 && p.fehler !== undefined) ? true : undefined}
            onChange={(ev) => aendern(i, { text: ev.target.value })} />
          {/* Das Kennzeichen ist seit P17 ein `Optionen mehrfach` mit EINER Option
              (me-optionen, Entscheid 40): die Gruppe heisst „Zeile n in die E-Rechnung“, das
              Kästchen „In E-Rechnung“ (sichtbar, auch am Telefon ohne Spaltenkopf).
              Nicht wählbar heisst gesperrt — der Fokus bleibt, die Vorlesestimme hört
              „nicht verfügbar“; me-optionen kennt kein disabled. */}
          <Optionen mehrfach aria-label={`Zeile ${i + 1} in die E-Rechnung`}
            wert={z.inInvoiceXml ? [IN_XML] : []}
            {...(z.kind !== "UNIT" || (imXml >= XML_ZEILEN_MAX && !z.inInvoiceXml)
              ? { gesperrt: true } : {})}
            onGeaendert={(ev) => aendern(i, { inInvoiceXml: (ev.werte ?? []).includes(IN_XML) })}>
            <option value={IN_XML}>In E-Rechnung</option>
          </Optionen>
          <LoeschKnopf className="adr__knopf"
            name={`Zeile ${i + 1} entfernen`}
            onClick={() => p.setDialog((d) => (d === null ? d
              : { ...d, lines: d.lines.filter((_, j) => j !== i) }))} />
          {p.zeilenFehler[i] !== undefined && (
            <span className="ki-feld__fehler adr__zeilenfehler">{p.zeilenFehler[i]}</span>)}
        </div>
      ))}
      <div className="adr__fuss">
        <KiKnopf className="adr__knopf"
          onClick={() => p.setDialog((d) => (d === null ? d : {
            ...d,
            lines: [...d.lines, { kind: "UNIT", text: "", inInvoiceXml: false }],
          }))}>
          Zeile hinzufügen
        </KiKnopf>
      </div>
      <p className="adr__hilfetext">
        Das Kennzeichen „In E-Rechnung" steht nur an Zeilen der Organisationseinheit.
        {imXml >= XML_ZEILEN_MAX
          && " Die E-Rechnung trägt höchstens zwei Anschriftzeilen."}
      </p>
      {p.fehler !== undefined && (
        <span className="ki-feld__fehler">{p.fehler}</span>)}
    </div>
  );
}
